import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Send,
  Lock,
} from 'lucide-angular';
import { AuthService } from '../../../../core/services/auth.service';
import { TranslatePipe } from '@ngx-translate/core';

type PasswordStep = 'phone' | 'otp' | 'success';

@Component({
  selector: 'app-settings-security',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TranslatePipe],
  templateUrl: './security.component.html',
})
export class SettingsSecurityComponent {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  AlertCircle = AlertCircle;
  CheckCircle = CheckCircle;
  Eye = Eye;
  EyeOff = EyeOff;
  Send = Send;
  Lock = Lock;

  // Parol o'zgartirish steps
  step = signal<PasswordStep>('phone');
  isLoading = signal(false);
  errorMsg = signal<string | null>(null);

  // Step 1
  phoneNumber = this.authService.user()?.phoneNumber ?? '';

  // Step 2
  otpCode = '';
  newPassword = '';
  confirmPassword = '';
  showNewPassword = signal(false);
  showConfirm = signal(false);

  sendOtp() {
    if (!this.phoneNumber.trim()) {
      this.errorMsg.set('Telefon raqamni kiriting');
      return;
    }
    this.isLoading.set(true);
    this.errorMsg.set(null);
    this.authService
      .forgotPassword({ phoneNumber: this.phoneNumber })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.step.set('otp');
          } else {
            this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'OTP yuborishda xatolik');
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          this.errorMsg.set(err?.error?.message ?? 'OTP yuborishda xatolik');
          this.isLoading.set(false);
        },
      });
  }

  resetPassword() {
    if (!this.otpCode.trim()) {
      this.errorMsg.set('Tasdiqlash kodini kiriting');
      return;
    }
    if (this.newPassword.length < 6) {
      this.errorMsg.set("Yangi parol kamida 6 ta belgidan iborat bo'lishi kerak");
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.errorMsg.set('Parollar mos kelmaydi');
      return;
    }
    this.isLoading.set(true);
    this.errorMsg.set(null);
    this.authService
      .resetPassword({
        phoneNumber: this.phoneNumber,
        otpCode: this.otpCode,
        newPassword: this.newPassword,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.step.set('success');
          } else {
            this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Parol yangilanmadi');
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          this.errorMsg.set(err?.error?.message ?? "Noto'g'ri kod yoki xatolik yuz berdi");
          this.isLoading.set(false);
        },
      });
  }

  restart() {
    this.step.set('phone');
    this.otpCode = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.errorMsg.set(null);
  }
}
